import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarCheck, CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

const selectCls =
  "h-10 w-full border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary";

const WEEK_ORDER = [
  "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira",
  "Sexta-feira", "Sábado", "Domingo",
];

const RELATIONSHIPS = [
  { value: "responsavel", label: "Responsável" },
  { value: "pai", label: "Pai" },
  { value: "mae", label: "Mãe" },
  { value: "avo", label: "Avó/Avô" },
  { value: "tutor", label: "Tutor(a)" },
  { value: "outro", label: "Outro" },
];

const CONSENT_TEXT =
  "Declaro que as informações fornecidas são verdadeiras e autorizo o IMJ a utilizá-las para análise e contato referente à matrícula.";

const EMPTY_FORM = {
  student_name: "", birth_date: "", student_notes: "",
  course_id: "", modality_id: "",
  contact_whatsapp: "", contact_email: "",
  guardian_name: "", guardian_whatsapp: "", guardian_email: "", guardian_relationship: "responsavel",
  consent: false,
};

function calcAge(iso) {
  if (!iso) return null;
  const b = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
  return age;
}

export default function PublicEnrollment() {
  const [courses, setCourses] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [settings, setSettings] = useState(null);
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    Promise.all([
      api.get("/public/courses"),
      api.get("/public/modalities"),
      api.get("/public/settings"),
    ])
      .then(([c, m, s]) => {
        setCourses(c.data);
        setModalities(m.data);
        setSettings(s.data);
      })
      .catch(() => setError("Não foi possível carregar os cursos. Recarregue a página."));
  }, []);

  useEffect(() => {
    setSlot(null);
    if (!form.course_id || !form.modality_id) {
      setSlots(null);
      return undefined;
    }
    let active = true;
    setSlotsLoading(true);
    api
      .get("/public/availability", { params: { course_id: form.course_id, modality_id: form.modality_id } })
      .then((r) => active && setSlots(r.data))
      .catch(() => active && setSlots([]))
      .finally(() => active && setSlotsLoading(false));
    return () => {
      active = false;
    };
  }, [form.course_id, form.modality_id]);

  const age = calcAge(form.birth_date);
  const isMinor = age !== null && age >= 0 && age < 18;

  const grouped = useMemo(() => {
    if (!slots) return [];
    const g = {};
    for (const s of slots) (g[s.weekday_name] ||= []).push(s);
    return WEEK_ORDER.filter((d) => g[d]).map((d) => [d, g[d]]);
  }, [slots]);

  const waDigits = (settings?.whatsapp || "").replace(/\D/g, "");
  const waLink = waDigits ? `https://wa.me/${waDigits.startsWith("55") ? waDigits : `55${waDigits}`}` : "#";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) {
      setError("É necessário aceitar o consentimento para enviar a solicitação.");
      return;
    }
    setSending(true);
    try {
      const payload = {
        student_name: form.student_name.trim(),
        birth_date: form.birth_date,
        student_notes: form.student_notes.trim() || null,
        course_id: form.course_id,
        modality_id: form.modality_id,
        preferred_slot: slot
          ? { weekday: slot.weekday, start_time: slot.start_time, end_time: slot.end_time }
          : null,
        guardian: isMinor
          ? {
              full_name: form.guardian_name.trim(),
              whatsapp: form.guardian_whatsapp,
              email: form.guardian_email.trim(),
              relationship: form.guardian_relationship,
            }
          : null,
        student_contact: isMinor
          ? null
          : { whatsapp: form.contact_whatsapp, email: form.contact_email.trim() || null },
        consent: true,
      };
      const { data } = await api.post("/public/enrollment-requests", payload);
      setDone(data.message);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="matricula-page">
      <header className="bg-[#0A0A0A] text-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-5 sm:px-6">
          <a href="/" className="font-heading text-2xl font-extrabold tracking-tighter" data-testid="matricula-logo">
            IMJ<span className="text-primary">.</span>
          </a>
          <p className="hidden text-xs uppercase tracking-[0.2em] text-zinc-400 sm:block">
            Instituto Musical Jacaré
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:py-14">
        {done ? (
          <div className="border border-border bg-white p-8 text-center sm:p-12" data-testid="matricula-success">
            <CheckCircle2 size={48} className="mx-auto text-primary" />
            <h1 className="mt-4 font-heading text-2xl font-bold sm:text-3xl">Solicitação enviada!</h1>
            <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground sm:text-base">{done}</p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button
                asChild
                className="bg-primary font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                data-testid="matricula-success-whatsapp"
              >
                <a href={waLink} target="_blank" rel="noreferrer">
                  <MessageCircle size={16} className="mr-2" /> Falar com a secretaria
                </a>
              </Button>
              <Button asChild variant="outline" className="font-semibold" data-testid="matricula-success-home">
                <a href="/">Voltar ao site</a>
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Matrículas abertas</p>
              <h1 className="mt-2 font-heading text-3xl font-bold sm:text-4xl" data-testid="matricula-title">
                Solicitação de matrícula
              </h1>
              <p className="mt-3 max-w-xl text-sm text-muted-foreground sm:text-base">
                Preencha os dados abaixo para solicitar sua matrícula no IMJ. Nossa secretaria analisará o pedido e
                entrará em contato para confirmar os próximos passos.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-8" data-testid="matricula-form">
              {error && (
                <div
                  className="flex items-start gap-2 border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
                  data-testid="matricula-error-alert"
                >
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <section className="border border-border bg-white p-5 sm:p-7">
                <h2 className="font-heading text-lg font-semibold" data-testid="matricula-section-aluno">
                  1. Dados do aluno
                </h2>
                <div className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="er-name">Nome completo do aluno *</Label>
                    <Input
                      id="er-name"
                      required
                      minLength={3}
                      maxLength={120}
                      value={form.student_name}
                      onChange={set("student_name")}
                      data-testid="matricula-name-input"
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="er-birth">Data de nascimento *</Label>
                      <Input
                        id="er-birth"
                        type="date"
                        required
                        value={form.birth_date}
                        onChange={set("birth_date")}
                        data-testid="matricula-birthdate-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="er-notes">Observações (opcional)</Label>
                    <textarea
                      id="er-notes"
                      rows={2}
                      maxLength={500}
                      value={form.student_notes}
                      onChange={set("student_notes")}
                      className="w-full border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Já estudou música? Alguma informação importante para a matrícula?"
                      data-testid="matricula-notes-input"
                    />
                  </div>
                </div>
              </section>

              <section className="border border-border bg-white p-5 sm:p-7">
                <h2 className="font-heading text-lg font-semibold" data-testid="matricula-section-curso">
                  2. Curso e horário de preferência
                </h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="er-course">Curso / instrumento *</Label>
                    <select
                      id="er-course"
                      required
                      className={selectCls}
                      value={form.course_id}
                      onChange={set("course_id")}
                      data-testid="matricula-course-select"
                    >
                      <option value="">Selecione o curso</option>
                      {courses.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="er-modality">Modalidade *</Label>
                    <select
                      id="er-modality"
                      required
                      className={selectCls}
                      value={form.modality_id}
                      onChange={set("modality_id")}
                      data-testid="matricula-modality-select"
                    >
                      <option value="">Selecione a modalidade</option>
                      {modalities.map((m) => (
                        <option key={m.id} value={m.id}>{m.name} — {m.duration_minutes} min</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-5" data-testid="matricula-slots">
                  <Label>Preferência de horário (opcional)</Label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Horários reais disponíveis neste momento. A escolha é apenas uma preferência — a confirmação
                    definitiva será feita pela secretaria.
                  </p>
                  {slotsLoading && (
                    <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground" data-testid="matricula-slots-loading">
                      <Loader2 className="h-4 w-4 animate-spin" /> Consultando horários...
                    </p>
                  )}
                  {!slotsLoading && slots && slots.length === 0 && (
                    <p className="mt-3 text-sm text-muted-foreground" data-testid="matricula-slots-empty">
                      Nenhum horário livre no momento para esta combinação. Envie mesmo assim — a secretaria
                      encontrará uma alternativa.
                    </p>
                  )}
                  {!slotsLoading && grouped.length > 0 && (
                    <div className="mt-3 space-y-4">
                      {grouped.map(([day, daySlots]) => (
                        <div key={day} data-testid={`matricula-slot-day-${day}`}>
                          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{day}</p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {daySlots.map((s) => {
                              const selected =
                                slot && slot.weekday === s.weekday && slot.start_time === s.start_time;
                              return (
                                <button
                                  key={`${s.weekday}-${s.start_time}`}
                                  type="button"
                                  onClick={() => setSlot(selected ? null : s)}
                                  className={`border px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                                    selected
                                      ? "border-primary bg-primary text-primary-foreground"
                                      : "border-foreground bg-white hover:border-primary"
                                  }`}
                                  data-testid={`matricula-slot-${s.weekday}-${s.start_time.replace(":", "")}`}
                                >
                                  {s.start_time} – {s.end_time}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              {age !== null && age >= 0 && (
                <section className="border border-border bg-white p-5 sm:p-7">
                  <h2 className="font-heading text-lg font-semibold" data-testid="matricula-section-contato">
                    3. {isMinor ? "Dados do responsável" : "Contato do aluno"}
                  </h2>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {isMinor
                      ? "Aluno menor de idade: informe os dados do responsável legal."
                      : "Informe como a secretaria pode falar com você."}
                  </p>
                  {isMinor ? (
                    <div className="mt-4 space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="er-gname">Nome completo do responsável *</Label>
                        <Input
                          id="er-gname"
                          required
                          minLength={3}
                          maxLength={120}
                          value={form.guardian_name}
                          onChange={set("guardian_name")}
                          data-testid="matricula-guardian-name-input"
                        />
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="er-gwa">WhatsApp do responsável *</Label>
                          <Input
                            id="er-gwa"
                            required
                            placeholder="(11) 99999-9999"
                            value={form.guardian_whatsapp}
                            onChange={set("guardian_whatsapp")}
                            data-testid="matricula-guardian-whatsapp-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="er-gemail">E-mail do responsável *</Label>
                          <Input
                            id="er-gemail"
                            type="email"
                            required
                            value={form.guardian_email}
                            onChange={set("guardian_email")}
                            data-testid="matricula-guardian-email-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="er-grel">Grau de parentesco *</Label>
                        <select
                          id="er-grel"
                          className={selectCls}
                          value={form.guardian_relationship}
                          onChange={set("guardian_relationship")}
                          data-testid="matricula-guardian-relationship-select"
                        >
                          {RELATIONSHIPS.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="er-wa">WhatsApp *</Label>
                        <Input
                          id="er-wa"
                          required
                          placeholder="(11) 99999-9999"
                          value={form.contact_whatsapp}
                          onChange={set("contact_whatsapp")}
                          data-testid="matricula-contact-whatsapp-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="er-email">E-mail</Label>
                        <Input
                          id="er-email"
                          type="email"
                          value={form.contact_email}
                          onChange={set("contact_email")}
                          data-testid="matricula-contact-email-input"
                        />
                      </div>
                    </div>
                  )}
                </section>
              )}

              <section className="border border-border bg-white p-5 sm:p-7">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="er-consent"
                    checked={form.consent}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, consent: v === true }))}
                    data-testid="matricula-consent-checkbox"
                  />
                  <Label htmlFor="er-consent" className="cursor-pointer text-sm font-normal leading-relaxed">
                    {CONSENT_TEXT}
                  </Label>
                </div>
                <Button
                  type="submit"
                  disabled={sending || !form.consent}
                  className="mt-6 w-full bg-primary py-6 font-heading text-base font-bold text-primary-foreground transition-colors hover:bg-primary/90"
                  data-testid="matricula-submit-button"
                >
                  {sending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <CalendarCheck size={18} className="mr-2" />
                  )}
                  Enviar solicitação de matrícula
                </Button>
                <p className="mt-3 text-center text-xs text-muted-foreground">
                  Nenhuma matrícula é criada automaticamente. A secretaria analisará sua solicitação.
                </p>
              </section>
            </form>
          </>
        )}
      </main>

      <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        Instituto Musical Jacaré — IMJ · Cabreúva/SP
      </footer>
    </div>
  );
}
